/*
  bare product buy box
  - Flavour swatches pick the variant; its photo moves to the front of the gallery.
  - Single / 2 / 3 pack multiplies the quantity; the shown pack saving is display only
    (the matching automatic discount is set up in Shopify admin).
  - One-time vs subscribe cards, with the subscription plan's own price.
  - Quantity + Add to cart sends one line, then opens the cart.
  - "Pair it with" buttons add another product without leaving the page.
*/
/* Show one gallery photo: on desktop only the active photo is visible; on phones the strip slides to it. */
function bareShowMedia(id) {
  const target = document.getElementById(id);
  if (!target) return;
  document.querySelectorAll('.bare-pdp__media').forEach((li) => li.classList.toggle('is-active', li === target));
  document.querySelectorAll('.bare-pdp__thumb').forEach((t) => {
    const on = t.dataset.target === id;
    t.classList.toggle('is-active', on);
    if (on) t.setAttribute('aria-current', 'true');
    else t.removeAttribute('aria-current');
  });
  const list = target.parentElement;
  if (list && list.scrollWidth > list.clientWidth) {
    list.scrollTo({ left: target.offsetLeft - list.offsetLeft });
  }
}

if (!customElements.get('bare-product-form')) {
  customElements.define(
    'bare-product-form',
    class BareProductForm extends HTMLElement {
      connectedCallback() {
        try {
          this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
        } catch (e) {
          this.variants = [];
        }
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.qtyInput = this.querySelector('[data-qty]');
        this.button = this.querySelector('[data-add]');
        this.error = this.querySelector('[data-error]');

        this.addEventListener('change', (e) => {
          if (e.target.name === 'bare_variant') this.onVariant();
          if (e.target.name === 'bare_pack') this.onPack();
          if (e.target.name === 'bare_plan' && this.pack().size === 1) this.wantsSub = this.subscribing();
          this.update();
        });
        this.wantsSub = this.subscribing();
        this.setupStickyBar();
        this.querySelectorAll('[data-step]').forEach((btn) =>
          btn.addEventListener('click', () => {
            const q = Math.max(1, Math.min(20, (parseInt(this.qtyInput.value, 10) || 1) + parseInt(btn.dataset.step, 10)));
            this.qtyInput.value = q;
            this.update();
          })
        );
        this.qtyInput.addEventListener('input', () => this.update());
        this.button.addEventListener('click', () => this.submit());
        this.update();
      }

      variant() {
        const checked = this.querySelector('input[name="bare_variant"]:checked');
        const id = checked ? parseInt(checked.value, 10) : null;
        return this.variants.find((v) => v.id === id) || this.variants[0];
      }

      pack() {
        const checked = this.querySelector('input[name="bare_pack"]:checked');
        return checked ? { size: parseInt(checked.value, 10), discount: parseFloat(checked.dataset.discount) || 0 } : { size: 1, discount: 0 };
      }

      planId() {
        const sel = this.querySelector('[data-plan-select]');
        return sel ? sel.value : null;
      }

      subscribing() {
        const checked = this.querySelector('input[name="bare_plan"]:checked');
        return !!(checked && checked.value === 'subscribe');
      }

      qty() {
        return Math.max(1, parseInt(this.qtyInput.value, 10) || 1);
      }

      /* Sticky add to cart bar: moved to <body> so no animated ancestor can break position: fixed,
         shown once the buy box's Add to cart button has scrolled up out of view. */
      setupStickyBar() {
        const bar = this.querySelector('[data-sticky-bar]');
        if (!bar) return;
        this.bar = bar;
        document.body.appendChild(bar);
        bar.querySelector('[data-sticky-add]').addEventListener('click', () => this.submit());
        bar.querySelectorAll('[data-sticky-variant]').forEach((pill) =>
          pill.addEventListener('click', () => {
            const input = this.querySelector(`input[name="bare_variant"][value="${pill.dataset.stickyVariant}"]`);
            if (!input || input.disabled) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          })
        );
        let ticking = false;
        const check = () => {
          ticking = false;
          const show = this.button.getBoundingClientRect().bottom < 0;
          if (show === bar.classList.contains('is-visible')) return;
          bar.classList.toggle('is-visible', show);
          bar.toggleAttribute('inert', !show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        };
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          setTimeout(check, 50);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        check();
      }

      updateStickyBar(v, total) {
        if (!this.bar) return;
        const pack = this.pack().size;
        const parts = [];
        if (this.variants.length > 1) parts.push(v.title);
        if (pack > 1) parts.push(pack + ' pack');
        parts.push(this.subscribing() ? (this.querySelector('[data-sub-option] .bare-pdp__option-title')?.firstChild?.textContent.trim() || 'Subscribe') : 'One-time');
        this.bar.querySelector('[data-sticky-sub]').textContent = parts.join(' · ');
        this.bar.querySelector('[data-sticky-price]').textContent = this.formatMoney(total);
        const add = this.bar.querySelector('[data-sticky-add]');
        add.disabled = !v.available;
        add.textContent = v.available ? 'Add to cart' : 'Sold out';
        this.bar.querySelectorAll('[data-sticky-variant]').forEach((pill) =>
          pill.classList.toggle('is-active', parseInt(pill.dataset.stickyVariant, 10) === v.id)
        );
      }

      /* Subscribe & save is only offered on the single pack: 2 and 3 packs switch to
         one-time, and going back to the single pack restores the shopper's choice. */
      onPack() {
        const subOption = this.querySelector('[data-sub-option]');
        if (!subOption) return;
        const single = this.pack().size === 1;
        const subInput = subOption.querySelector('input[name="bare_plan"]');
        const onceInput = this.querySelector('input[name="bare_plan"][value=""]');
        subOption.hidden = !single;
        subInput.disabled = !single;
        const note = this.querySelector('[data-sub-note]');
        if (note) note.hidden = single;
        if (!single) onceInput.checked = true;
        else if (this.wantsSub) subInput.checked = true;
      }

      onVariant() {
        const v = this.variant();
        const name = this.querySelector('[data-variant-name]');
        if (name) name.textContent = v.title;
        if (v.media) bareShowMedia('BarePdpMedia-' + v.media);
        /* [[default|Variant title=value]] tokens in the details and ingredient list */
        document.querySelectorAll('[data-by-variant]').forEach((el) => {
          const [fallback, ...pairs] = el.dataset.byVariant.split('|');
          const match = pairs.find((p) => p.slice(0, p.indexOf('=')) === v.title);
          el.textContent = match ? match.slice(match.indexOf('=') + 1) : fallback;
        });
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }

      update() {
        /* Subscribe & save is single-unit only: lock the quantity at 1 while subscribing */
        const subscribing = this.subscribing();
        if (subscribing) this.qtyInput.value = 1;
        this.qtyInput.readOnly = subscribing;
        this.querySelectorAll('[data-step]').forEach((btn) => (btn.disabled = subscribing));

        const v = this.variant();
        const pack = this.pack();
        const factor = 1 - pack.discount / 100;
        const units = pack.size;
        const once = Math.round(v.price * units * factor);
        const onceFull = v.price * units;
        const planPrice = this.planId() && v.plans[this.planId()] !== undefined ? v.plans[this.planId()] : v.price;
        const sub = Math.round(planPrice * units * factor);

        const set = (sel, text, show = true) => {
          const el = this.querySelector(sel);
          if (!el) return;
          el.textContent = text;
          el.hidden = !show;
        };
        set('[data-once-price]', this.formatMoney(once));
        set('[data-once-was]', this.formatMoney(onceFull), pack.discount > 0);
        set('[data-once-off]', pack.discount + '% off', pack.discount > 0);
        set('[data-sub-price]', this.formatMoney(sub));
        set('[data-sub-was]', this.formatMoney(onceFull), sub < onceFull);

        /* Price and per-serving price under the title follow the selected option */
        const selected = this.subscribing() ? sub : once;
        const info = this.closest('.bare-pdp__info') || document;
        const titlePrice = info.querySelector('[data-title-price]');
        if (titlePrice) titlePrice.textContent = this.formatMoney(selected);
        const servingPrice = info.querySelector('[data-serving-price]');
        const servings = parseInt(info.querySelector('[data-servings]')?.dataset.servings || '0', 10);
        if (servingPrice && servings > 0) servingPrice.textContent = this.formatMoney(Math.round(selected / (servings * units)));

        const total = selected * this.qty();
        set('[data-add-price]', v.available ? '· ' + this.formatMoney(total) : '');
        set('[data-add-label]', v.available ? 'Add to cart' : 'Sold out');
        this.button.disabled = !v.available;
        if (this.error) this.error.hidden = true;
        this.updateStickyBar(v, total);
      }

      formatMoney(cents) {
        const value = (cents / 100).toFixed(2);
        const parts = value.split('.');
        const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return this.moneyFormat
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas.replace(/,/g, '.') + ',' + parts[1])
          .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1]);
      }

      async submit() {
        const v = this.variant();
        if (!v || !v.available) return;
        const item = { id: v.id, quantity: this.pack().size * this.qty() };
        if (this.subscribing() && this.planId()) item.selling_plan = parseInt(this.planId(), 10);
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        const drawer = document.querySelector('cart-drawer');
        const body = { items: [item] };
        if (drawer) body.sections = drawer.getSectionsToRender().map((s) => s.id);
        try {
          const res = await fetch(this.dataset.cartAdd + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
          if (!bareOpenCartDrawer(drawer, data, this.button)) {
            window.location.href = this.dataset.cart;
            return;
          }
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        } catch (err) {
          this.error.textContent = err.message;
          this.error.hidden = false;
          if (this.bar && this.bar.classList.contains('is-visible')) this.error.scrollIntoView({ block: 'center' });
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
        }
      }
    }
  );
}

/* With the slide-out cart on, refresh it from an add-to-cart response and open it. Returns false when there is no drawer. */
function bareOpenCartDrawer(drawer, data, opener) {
  if (!drawer || !data.sections) return false;
  drawer.classList.remove('is-empty');
  drawer.setActiveElement(opener);
  drawer.renderContents({ id: data.items && data.items[0] ? data.items[0].id : undefined, sections: data.sections });
  return true;
}

document.addEventListener('click', async (e) => {
  const thumb = e.target.closest('.bare-pdp__thumb');
  if (thumb) {
    bareShowMedia(thumb.dataset.target);
    return;
  }
  const pair = e.target.closest('[data-pair-add]');
  if (pair) {
    pair.disabled = true;
    const label = pair.textContent;
    const drawer = document.querySelector('cart-drawer');
    const body = { items: [{ id: parseInt(pair.dataset.pairAdd, 10), quantity: 1 }] };
    if (drawer) body.sections = drawer.getSectionsToRender().map((s) => s.id);
    try {
      const res = await fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      bareOpenCartDrawer(drawer, await res.json(), pair);
      pair.textContent = 'Added ✓';
      setTimeout(() => {
        pair.textContent = label;
        pair.disabled = false;
      }, 2500);
    } catch (err) {
      pair.textContent = 'Try again';
      pair.disabled = false;
    }
  }
});

/* Gallery hover zoom: on mouse devices the photo enlarges around the cursor (styles in section-bare-product.css). */
document.addEventListener('pointermove', (e) => {
  if (e.pointerType !== 'mouse') return;
  const media = e.target.closest('.bare-pdp__media');
  if (!media) return;
  const box = media.getBoundingClientRect();
  media.style.setProperty('--zoom-x', `${((e.clientX - box.left) / box.width) * 100}%`);
  media.style.setProperty('--zoom-y', `${((e.clientY - box.top) / box.height) * 100}%`);
});
